import { createContext, useContext, useMemo } from "react";

/** The breadcrumb title setter function. */
export interface BreadcrumbTitleSetter {

  /** Sets the current breadcrumb title. */
  (title: string | null): void;
}

/** Resolves a breadcrumb path to its final form. */
export interface BreadcrumbPathResolver {

  /**
   * Returns the final path for the specified breadcrumb path.
   * @param path The breadcrumb path to resolve.
   */
  (path: string): string;
}

/**
 * A React hook that returns a path resolver for the position in the component
 * tree from which it is called. This allows resolution to depend on contextual
 * information (such as the owning module) available where breadcrumbs register.
 */
export interface BreadcrumbPathResolverHook {

  /** Returns a path resolver for the calling position in the tree. */
  (): BreadcrumbPathResolver;
}

/** Breadcrumb data. */
export interface BreadcrumbData {

  /** The breadcrumb title. */
  readonly title: string;

  /** The breadcrumb path. */
  readonly path: string;
}

/** A breadcrumb data entry. */
export interface RegisteredBreadcrumbData extends BreadcrumbData {

  /** The ID of the breadcrumb parent entry. */
  readonly parentIndex: number | null;
}

/** The breadcrumb context data. */
export interface BreadcrumbContextData {

  /** The current breadcrumb nodes. */
  nodes: ReadonlyArray<RegisteredBreadcrumbData>;

  /** The current title override. */
  titleOverride: { pathname: string; title: string } | null;

  /** Returns a path resolver for the calling position in the tree. */
  usePathResolver: BreadcrumbPathResolverHook;

  /** Sets the current items. */
  setItems(items: ReadonlyArray<RegisteredBreadcrumbData>): () => void;

  /** Sets the current title. */
  setTitle: BreadcrumbTitleSetter;
}

/** The breadcrumb context. */
export const BreadcrumbContext = createContext<BreadcrumbContextData>({
  nodes: [],
  titleOverride: null,
  usePathResolver: () => (path) => path,
  setItems: () => (): void => {
    /* Do nothing */
  },
  setTitle: () => {
    /* Do nothing */
  },
});

/**
 * Provides methods for the manipulation of the current breadcrumb if any.
 * @returns A tuple containing a function for setting the current breadcrumb title.
 */
export function useCurrentBreadcrumb(): [BreadcrumbTitleSetter] {
  const { setTitle } = useContext(BreadcrumbContext);

  return useMemo((): [BreadcrumbTitleSetter] => {
    return [setTitle];
  }, [setTitle]);
}
